import { format, parseISO, subDays } from 'date-fns';
import {
  policyType,
  renewalGenerationBixOnlyStates,
  renewalGenerationBlocks,
  renewalGenerationDaysBeforeEndDate
} from '@ourbranch/lookups';

// Home, condo and renters share one window in the renewals lookup. Farm has no entry (different renewal flow).
const renewalLookupPolicyType = {
  [policyType.Auto]: policyType.Auto,
  [policyType.Home]: policyType.Home,
  [policyType.Condo]: policyType.Home,
  [policyType.Renters]: policyType.Home
};

/**
 * Dates the renewals cron will try to generate this policy's renewal, as yyyy-MM-dd { from, to }:
 * `from` is the first day it is picked up, `to` the last day a failed attempt is retried. (#34196)
 * Null when no renewal is coming: farm, auto-renew off, a cancelled term, or a state or carrier exit the cron skips.
 * Does not check whether the renewal already exists; the caller shows a link to it instead.
 */
export const getRenewalGenerationWindow = (policy) => {
  const lookupPolicyType = renewalLookupPolicyType[policy?.policyType];
  // The renewals model renews WV from the 4th term on even with auto-renew off.
  const renewsAutomatically = policy?.autoRenew !== false || (policy?.state === 'WV' && policy?.term >= 4);
  if (
    !policy?.endDate ||
    !lookupPolicyType ||
    !renewsAutomatically ||
    (renewalGenerationBixOnlyStates.includes(policy.state) && !policy.isBix) ||
    renewalGenerationBlocks.some(
      (block) =>
        block.states.includes(policy.state) &&
        (!block.policyTypes || block.policyTypes.includes(policy.policyType)) &&
        policy.endDate >= block.endDateOnOrAfter // yyyy-MM-dd strings compare in date order
    ) ||
    // fullTermPolicyEndDate can be null; fall back to endDate like getPolicyStatus does.
    policy.endDate !== (policy.fullTermPolicyEndDate || policy.endDate)
  ) {
    return null;
  }

  const { first, last } = (renewalGenerationDaysBeforeEndDate[policy.state] ??
    renewalGenerationDaysBeforeEndDate.DEFAULT)[lookupPolicyType];
  const endDate = parseISO(policy.endDate);
  return {
    from: format(subDays(endDate, first), 'yyyy-MM-dd'),
    to: format(subDays(endDate, last), 'yyyy-MM-dd')
  };
};
